import { useEffect, useState } from 'react';
import { TRACKS } from '../../constants/appConstants.js';
import {
  adminListLearners, adminMoodTrend, adminDecisionTraces, adminCohortMemory,
  deriveOverview, weeklyBuckets, countBy,
} from '../../services/adminClient.js';
import { T, card, sectionTitle, PageHeader, StatCard, LineChart, BarRow, Donut } from './ui.jsx';

const trackName = (id) => TRACKS[id]?.label || id || '—';
const MOOD_COLOR = { stressed: T.coral, tired: '#f59e0b', okay: T.slate, good: '#34d399', motivated: T.emerald };

export default function AdminAnalytics() {
  const [learners, setLearners] = useState(null);
  const [moodRows, setMoodRows] = useState([]);
  const [traces, setTraces] = useState([]);
  const [cohort, setCohort] = useState([]);

  useEffect(() => {
    let c = false;
    Promise.all([adminListLearners(), adminMoodTrend(30), adminDecisionTraces(56), adminCohortMemory()])
      .then(([l, m, t, ch]) => { if (c) return; setLearners(l || []); setMoodRows(m || []); setTraces(t || []); setCohort(ch || []); })
      .catch(() => { if (!c) setLearners([]); });
    return () => { c = true; };
  }, []);

  if (!learners) return <p style={{ color: T.slate, fontSize: 13 }}>Loading analytics…</p>;

  const ov = deriveOverview(learners);
  const signups = weeklyBuckets(learners, 'created_at', 8);
  const tracks  = countBy(learners.map(l => ({ t: trackName(l.active_track_id || l.current_track) })), 't');
  const traceWeekly = weeklyBuckets(traces, 'created_at', 8);

  const subSlices = [
    { key: 'Paid (active)', n: learners.filter(l => l.subscription_status === 'active').length, color: T.emerald },
    { key: 'Trialing',      n: learners.filter(l => l.subscription_status === 'trialing').length, color: T.amber },
    { key: 'Other',         n: learners.filter(l => !['active', 'trialing'].includes(l.subscription_status)).length, color: T.slate },
  ].filter(s => s.n > 0);

  // mood trend: count by mood across the window
  const moodCounts = countBy(moodRows, 'mood').map(r => ({ ...r, color: MOOD_COLOR[r.key] || T.slate }));
  const activeBuckets = cohort.filter(c => c.sample_size >= 8).length;

  const col = { flex: '1 1 340px', minWidth: 0, display: 'flex', flexDirection: 'column', gap: 16 };

  return (
    <div>
      <PageHeader title="Analytics" subtitle="Business pulse — real Supabase data only." />

      <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', marginBottom: 16 }}>
        <StatCard label="Total Learners" value={ov.total} tint={T.amber} />
        <StatCard label="Paid Users" value={ov.paid} tint={T.emerald} />
        <StatCard label="Active (7 days)" value={ov.active7} tint={T.amber} />
        <StatCard label="Free → Paid" value={`${ov.trialToPaid}%`} tint={T.emerald} />
      </div>

      <div style={{ display: 'flex', gap: 16, alignItems: 'flex-start', flexWrap: 'wrap' }}>
        <div style={col}>
          <div style={card}>
            <p style={{ ...sectionTitle, marginBottom: 12 }}>Subscription Breakdown</p>
            <div style={{ display: 'flex', alignItems: 'center', gap: 18, flexWrap: 'wrap' }}>
              <Donut slices={subSlices.length ? subSlices : [{ key: '—', n: 1, color: '#eef0f4' }]} />
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {subSlices.map(s => (
                  <div key={s.key} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12.5 }}>
                    <span style={{ width: 10, height: 10, borderRadius: 3, background: s.color }} />
                    <span style={{ color: T.slate }}>{s.key}</span>
                    <span style={{ fontWeight: 800, color: T.ink }}>{s.n}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
          <div style={card}>
            <p style={{ ...sectionTitle, marginBottom: 4 }}>Signups Over Time</p>
            <p style={{ fontSize: 12, color: T.slate, margin: '0 0 12px' }}>New learners per week (8 weeks)</p>
            <LineChart points={signups} color={T.amber} />
          </div>
          <div style={card}>
            <p style={{ ...sectionTitle, marginBottom: 12 }}>Track Distribution</p>
            <BarRow rows={tracks} color={T.amber} />
          </div>
        </div>

        <div style={col}>
          <div style={card}>
            <p style={{ ...sectionTitle, marginBottom: 12 }}>Mood Trend (30 days)</p>
            <BarRow rows={moodCounts} color={T.emerald} />
          </div>
          <div style={card}>
            <p style={{ ...sectionTitle, marginBottom: 4 }}>Decision Trace Volume</p>
            <p style={{ fontSize: 12, color: T.slate, margin: '0 0 12px' }}>Engine turns per week (demo excluded)</p>
            <LineChart points={traceWeekly} color={T.emerald} />
          </div>
          <div style={card}>
            <p style={{ ...sectionTitle, marginBottom: 12 }}>Cohort Memory Coverage</p>
            <div style={{ display: 'flex', gap: 24 }}>
              <div><p style={{ fontSize: 26, fontWeight: 800, color: T.emerald, margin: 0 }}>{activeBuckets}</p><p style={{ fontSize: 11, color: T.slate, margin: 0 }}>active (N ≥ 8)</p></div>
              <div><p style={{ fontSize: 26, fontWeight: 800, color: T.ink, margin: 0 }}>{cohort.length}</p><p style={{ fontSize: 11, color: T.slate, margin: 0 }}>total buckets</p></div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
